import type BaseModel from "../../../src/models/BaseModel";
import ObjectFactory from "../../../src/models/ObjectFactory";
import type { C1_EloItemInstanceData } from "../types/C1_EloTypes";
import type { C1_LegacyEloItemTreeData } from "../types/C1_EloTypes";
import C1_EloItem, { migrateLegacyEloItemTreeNode } from "./C1_EloItem";
import Group from "../../../src/models/Group";
import { C1_Consumer } from "./C1_Consumer";
import { C1_Button } from "./C1_Button";

/** C1 electrical hierarchy attached to a typed ESTADA Group. */
export class C1_EloGroup extends C1_EloItem {
  private temporaryItems?: C1_EloItem[];
  override get owner(): Group | undefined {
    return super.owner as Group | undefined;
  }

  override get className(): string {
    return "C1_EloGroup";
  }

  /** Updates repeat metadata without replacing the group's existing devices. */
  setItemCount(itemCount: number): void {
    if (this.data.details?.itemCount === itemCount) return;
    this.beforeChange("details");
    this._data.details = { ...this._data.details, itemCount };
    this.changed("details");
  }

  /** Direct typed children backed by the ESTADA group collections. */
  get items(): C1_EloItem[] {
    if (this.temporaryItems) return this.temporaryItems;
    const result: C1_EloItem[] = [];
    if (!this.owner) return result;
    for (const group of this.owner.groups) {
      if (group.c1 instanceof C1_EloGroup) result.push(group.c1);
    }
    for (const consumer of this.owner.consumers) {
      if (this.definition?.definitionId === "room" && consumer.c1 instanceof C1_Consumer && consumer.c1.data.lightGroupFurniture) continue;
      if (consumer.c1 instanceof C1_Consumer) result.push(consumer.c1);
    }
    for (const button of this.owner.buttons) {
      if (button.c1 instanceof C1_Button) result.push(button.c1);
    }
    return result;
  }

  /** Installs runtime-only preview children without mutating ESTADA collections. */
  setTemporaryItems(items: C1_EloItem[] | undefined): void {
    this.temporaryItems = items;
  }

  get allItems(): C1_EloItem[] {
    const result: C1_EloItem[] = [this];
    for (const item of this.items) {
      if (item instanceof C1_EloGroup) result.push(...item.allItems);
      else result.push(item);
    }
    return result;
  }

  findItem(id: string): C1_EloItem | undefined {
    if (this.owner?.className === "Consumer") {
      const child = this.items.find(item => item.id === id);
      if (child) return child;
    }
    for (const item of this.allItems) {
      if (item.owner?.id === id) return item;
    }
    return undefined;
  }

  /** Builds the old nested DTO only at compatibility boundaries. */
  override toLegacyTreeData(): C1_LegacyEloItemTreeData {
    const data = super.toLegacyTreeData();
    for (const item of this.items) data.items.push(item.toLegacyTreeData());
    return data;
  }

  protected override get placementChildren(): C1_EloItem[] {
    return this.items;
  }
}

ObjectFactory.registerClass("c1:C1_EloGroup", (data: C1_EloItemInstanceData | C1_LegacyEloItemTreeData, owner?: BaseModel) =>
  new C1_EloGroup(migrateLegacyEloItemTreeNode(data, owner!), owner!) as unknown as BaseModel,
);
